'use client';

import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../../components/ui/card";
import { ContentContainer } from '../content';
import { PageHero } from '../content/page-hero';
import { Database, Github, Table2, ExternalLink, BarChart, Download, Plus, HardDrive, Upload, Trash2 } from 'lucide-react';
import { getAllDemoDatasets } from '../../lib/demo-datasets';
import { Format } from '../../lib/formats';
import { createClient } from '@/lib/supabase/client';
import { Dataset } from '@/lib/dataset/types';
import { useNavigateToViewer } from '@/hooks/use-navigate-to-viewer';
import { CreateDatasetFromFilesModal } from '../dataset/create-dataset-from-files-modal';
import { IndexedDBFileStore } from '@/lib/engine/core/file-store';
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import useSWR from 'swr';
import { Session } from '@supabase/supabase-js';
import { useDatasets } from '@/lib/datasets/use-datasets';
import { useLocalDatasets, LocalDataset } from '@/lib/datasets/use-local-datasets';
import { useUser } from '@/hooks/use-user';
import { UpgradeModal } from '../subscription/upgrade-modal';

// Simplified version of the DatasetCard component
function SampleDatasetCard({ dataset }: { dataset: { id: string; name: string; description: string; rows: number } }) {
  const { viewSample, isProcessing } = useNavigateToViewer();
  
  return (
    <Card key={dataset.id} className="flex flex-col h-full">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Table2 className="h-4 w-4 text-blue-500" />
          <span className="truncate" title={dataset.name}>{dataset.name}</span>
        </CardTitle>
        <CardDescription>
          {dataset.rows.toLocaleString()} rows
        </CardDescription>
      </CardHeader>
      <CardContent className="text-sm flex-grow">
        <p>{dataset.description}</p>
      </CardContent>
      <CardFooter className="mt-auto">
        <div className="w-full">
          <Button 
            variant="default" 
            size="sm" 
            className="w-full"
            asChild
            disabled={isProcessing}
          >
            <Link href={`/app?sample-id=${encodeURIComponent(dataset.id)}`} className="no-underline">
              {isProcessing ? 'Loading...' : 'View Dataset'}
            </Link>
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}

// For a user's own dataset
function UserDatasetCard({ 
  dataset, 
  onDelete 
}: { 
  dataset: Dataset;
  onDelete: (datasetId: string) => Promise<void>;
}) {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(dataset.id);
    } catch (error) {
      console.error('Error deleting dataset:', error);
      setIsDeleting(false);
    }
  };

  return (
    <Card key={dataset.id} className="flex flex-col h-full">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Table2 className="h-4 w-4 text-blue-500" />
          <span className="truncate" title={dataset.name || 'Untitled Dataset'}>
            {dataset.name || 'Untitled Dataset'}
          </span>
        </CardTitle>
        <CardDescription>
          {dataset.total_rows?.toLocaleString() || 'Unknown'} rows
        </CardDescription>
      </CardHeader>
      <CardContent className="text-sm flex-grow">
        <p>{dataset.description || 'No description'}</p>
      </CardContent>
      <CardFooter className="mt-auto">
        <div className="w-full flex gap-2">
          <Button 
            variant="default" 
            size="sm" 
            className="flex-1"
            asChild
          >
            <Link href={`/datasets/${dataset.id}/${dataset.slug}`} className="no-underline">
              View Dataset
            </Link>
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                disabled={isDeleting}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-4">
              <div className="flex flex-col gap-4">
                <div>
                  <h4 className="font-medium mb-1">Delete Dataset</h4>
                  <p className="text-sm text-muted-foreground">Are you sure you want to delete this dataset? This action cannot be undone.</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleDelete}
                    disabled={isDeleting}
                  >
                    {isDeleting ? 'Deleting...' : 'Delete'}
                  </Button>
                  <PopoverPrimitive.Close asChild>
                    <Button variant="outline" size="sm">Cancel</Button>
                  </PopoverPrimitive.Close>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </CardFooter>
    </Card>
  );
}

// For a local dataset from IndexedDB
function LocalDatasetCard({ dataset, onRemove }: { dataset: LocalDataset; onRemove: () => void }) {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleRemove = async () => {
    setIsDeleting(true);
    try {
      await onRemove();
    } catch (error) {
      console.error('Error removing file:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Card key={dataset.id} className="flex flex-col h-full">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <HardDrive className="h-4 w-4 text-green-500" />
          <span className="truncate" title={dataset.name}>{dataset.name}</span>
        </CardTitle>
        <CardDescription>
          {dataset.size} • {dataset.lastModified}
        </CardDescription>
      </CardHeader>
      <CardContent className="text-sm flex-grow">
        <p>Local file stored on your computer</p>
      </CardContent>
      <CardFooter className="mt-auto">
        <div className="w-full flex gap-2">
          <Button 
            variant="default" 
            size="sm" 
            className="flex-1"
            asChild
          >
            <Link href={`/app?file-id=${encodeURIComponent(dataset.id)}`} className="no-underline">
              View
            </Link>
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                disabled={isDeleting}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-4">
              <div className="flex flex-col gap-4">
                <div>
                  <h4 className="font-medium mb-1">Remove File</h4>
                  <p className="text-sm text-muted-foreground">Are you sure you want to remove this file from the list? The file will not be deleted from your computer.</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleRemove}
                    disabled={isDeleting}
                  >
                    {isDeleting ? 'Removing...' : 'Remove'}
                  </Button>
                  <PopoverPrimitive.Close asChild>
                    <Button variant="outline" size="sm">Cancel</Button>
                  </PopoverPrimitive.Close>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </CardFooter>
    </Card>
  );
}

// Banner to create a new dataset when the user has none
function CreateDatasetBanner({ onCreateClick }: { onCreateClick: () => void }) {
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5" />
          Create Your First Dataset
        </CardTitle>
      </CardHeader>
      <CardContent className="pb-2">
        <p className="text-sm text-muted-foreground">
          Get started by creating your first dataset. You can upload your own data files
          or import data from various sources.
        </p>
      </CardContent>
      <CardFooter>
        <Button onClick={onCreateClick}>
          <Plus className="h-4 w-4 mr-2" />
          Create Dataset
        </Button>
      </CardFooter>
    </Card>
  );
}

// Banner for non-logged in users
function LoginPrompt({ onUpgradeClick }: { onUpgradeClick: () => void }) {
  return (
    <Card className="mb-8">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5" />
          Create Your Own Datasets
        </CardTitle>
      </CardHeader>
      <CardContent className="pb-2">
        <p className="text-sm text-muted-foreground">
          TabLab lets you create your own datasets, upload files, and analyze data.
          Sign in to get started with your own collection of datasets.
        </p>
      </CardContent>
      <CardFooter>
        <Button 
          variant="default"
          className="w-full sm:w-auto"
          onClick={onUpgradeClick}
        >
          <Database className="h-4 w-4 mr-2" />
          Sign In to Create Datasets
        </Button>
      </CardFooter>
    </Card>
  );
}

// Add this fetcher function near the top of the file, before the component
const fetcher = async (url: string) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Failed to fetch data');
  }
  return response.json();
};

export function DatasetsLandingPage() {
  const router = useRouter();
  const demoDatasets = getAllDemoDatasets();
  
  // Use the proper useUser hook instead of manual session management
  const { user, isLoading: isLoadingUser } = useUser();

  // Use the useDatasets hook
  const { datasets: userDatasets, isLoading: isLoadingDatasets, deleteDataset } = useDatasets();
  const { localDatasets, isLoading: isLoadingLocal, removeLocalDataset, refresh: refreshLocalDatasets } = useLocalDatasets();

  const handleDeleteDataset = async (datasetId: string) => {
    const result = await deleteDataset(datasetId);
    if (!result.success) {
      throw result.error;
    }
  };
  
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  
  // Handle search filtering
  const filteredLocalDatasets = searchTerm.trim() 
    ? localDatasets.filter(dataset => dataset.name.toLowerCase().includes(searchTerm.toLowerCase()))
    : localDatasets;
  
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleUpgradeClick = () => {
    setIsUpgradeModalOpen(true);
  };
  
  return (
    <div className="h-full">
      <PageHero 
        title="Datasets"
        description="Browse and analyze datasets in TabLab."
        variant="tablab"
        noBorder={true}
      >
        <ContentContainer className="pt-2 pb-8">
          {/* User Datasets Section */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold" id="datasets-section">Your Datasets</h2>
              {user && (
                <Button onClick={() => setIsCreateModalOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Dataset
                </Button>
              )}
            </div>
            
            {!user ? (
              <LoginPrompt onUpgradeClick={handleUpgradeClick} />
            ) : (isLoadingUser || isLoadingDatasets) ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : userDatasets?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {userDatasets.map(dataset => (
                  <UserDatasetCard 
                    key={dataset.id} 
                    dataset={dataset} 
                    onDelete={handleDeleteDataset}
                  />
                ))}
                
                <Card className="flex flex-col h-full border-dashed rounded-lg hover:shadow-lg hover:shadow-muted transition-shadow">
                  <CardContent className="flex items-center justify-center flex-grow p-0">
                    <Button 
                      variant="ghost" 
                      className="h-full w-full flex flex-col gap-3 p-6 rounded-lg hover:bg-muted/50"
                      onClick={() => setIsCreateModalOpen(true)}
                    >
                      <Plus className="h-8 w-8" />
                      <span>Create New Dataset</span>
                    </Button>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <CreateDatasetBanner onCreateClick={() => setIsCreateModalOpen(true)} />
            )}
          </div>
          
          {/* Files Opened from Your Computer Section */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold" id="on-my-computer">Files Opened from Your Computer</h2>
              <Button 
                variant="outline"
                asChild
              >
                <Link href="/app" className="no-underline flex items-center">
                  <Upload className="h-4 w-4 mr-2" />
                  Open File
                </Link>
              </Button>
            </div>
            
            {isLoadingLocal ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : localDatasets.length > 0 ? (
              <div className="max-h-[600px] overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {filteredLocalDatasets.map(dataset => (
                    <LocalDatasetCard 
                      key={dataset.id} 
                      dataset={dataset} 
                      onRemove={() => removeLocalDataset(dataset.id)}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <HardDrive className="h-5 w-5" />
                    No Files Opened
                  </CardTitle>
                </CardHeader>
                <CardContent className="pb-2">
                  <p className="text-sm text-muted-foreground">
                    Open CSV, JSON, or other data files to view and analyze them directly on your computer.<br />
                    Files opened here are stored locally and never leave your computer.
                  </p>
                </CardContent>
                <CardFooter>
                  <Button variant="default" asChild>
                    <Link href="/app" className="no-underline">
                      <Upload className="h-4 w-4 mr-2" />
                      Open File
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            )}
          </div>
          
          {/* Create Dataset Modal */}
          <CreateDatasetFromFilesModal
            open={isCreateModalOpen}
            onOpenChange={setIsCreateModalOpen}
          />
          
          {/* Upgrade Modal */}
          <UpgradeModal
            open={isUpgradeModalOpen}
            onClose={() => setIsUpgradeModalOpen(false)}
            openTrigger="create_dataset"
          />
          
          {/* GitHub Datasets Banner Card */}
          <Card className="mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border-blue-200 dark:border-blue-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Github className="h-5 w-5" />
                GitHub Datasets Browser
              </CardTitle>
              <CardDescription>
                Browse, view, and analyze CSV files from any GitHub repository
              </CardDescription>
            </CardHeader>
            <CardContent className="pb-2">
              <p className="text-sm text-muted-foreground">
                Access CSV files directly from any public GitHub repository.
                No need to download or clone the repo - view and analyze data instantly in your browser!
              </p>
            </CardContent>
            <CardFooter>
              <Button 
                variant="default"
                className="w-full sm:w-auto"
                asChild
              >
                <Link href="/datasets/github" className="no-underline flex items-center">
                  <Github className="h-4 w-4 mr-2" />
                  Browse GitHub Datasets
                </Link>
              </Button>
            </CardFooter>
          </Card>
          
          {/* Sample Datasets Section */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Sample Datasets</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {demoDatasets.slice(0, 6).map((dataset) => (
                <SampleDatasetCard key={dataset.id} dataset={dataset} />
              ))}
            </div>
          </div>
          
          {/* Use Cases Section */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">What You Can Do</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <BarChart className="h-4 w-4 text-green-500" />
                    Data Visualization
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm">
                  <p>Create charts, graphs, and interactive visualizations to explore patterns in your data.</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Database className="h-4 w-4 text-purple-500" />
                    Data Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm">
                  <p>Filter, sort, and transform data to extract valuable insights and make informed decisions.</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Github className="h-4 w-4 text-blue-500" />
                    GitHub Integration
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm">
                  <p>Access data directly from GitHub repositories without downloading files or cloning repositories.</p>
                </CardContent>
              </Card>
            </div>
          </div>
          
          <div className="mt-8 p-4 bg-muted rounded-lg">
            <h3 className="text-lg font-medium mb-2">About TabLab Datasets</h3>
            <p className="text-sm text-muted-foreground">
              TabLab makes it easy to explore, analyze, and visualize tabular data from various sources.
              Whether you're accessing sample datasets or data from public repositories,
              you can quickly gain insights using our powerful analysis tools.
            </p>
          </div>
        </ContentContainer>
      </PageHero>
    </div>
  );
} 